import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import Table from '../../../components/Table/Table';
import AlertModal from '../../../components/AlertModal/AlertModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import Button from '../../../components/Button/Button';
import { Modal } from '../../../components/Modal/Modal';
import { DynamicInput, DynamicSelect, DynamicCheckbox } from '../../../components/Inputs/Inputs';
import { ReactComponent as Edit } from '../../../assets/edit-3.svg';
import { ReactComponent as Close } from '../../../assets/x.svg';
import './SyncContent.scss';

const TABS = [
  { value: 'estado', label: 'Estado' },
  { value: 'fontes', label: 'Fontes de Eventos & Promoções' },
];

const STATUS_LABEL = {
  running: 'A sincronizar…',
  success: 'Sucesso',
  success_with_warnings: 'Sucesso com avisos',
  error: 'Erro',
};

const STATUS_BADGE_CLASS = {
  running: 'running',
  success: 'on',
  success_with_warnings: 'warning',
  error: 'off',
};

const SYNC_TYPE_LABEL = {
  website: 'Website',
  events_promotions: 'Eventos & Promoções',
};

const initialSourceForm = { url_pattern: '', description: '', is_active: true, hotel_id: '' };

function extractErrorMessage(err, fallback = 'Ocorreu um erro') {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error_code || fallback;
  }
  return detail || err?.message || fallback;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString('pt-PT') : '—';
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${rest}s`;
}

export default function SyncContent() {
  const { axiosAuth } = useContext(AuthContext);

  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [tab, setTab] = useState('estado');

  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [sources, setSources] = useState([]);

  const [triggering, setTriggering] = useState(false);

  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });
  const openError = useCallback((title, err) => {
    setErrorModal({ open: true, title, message: extractErrorMessage(err) });
  }, []);
  const closeError = useCallback(() => setErrorModal({ open: false, title: '', message: '' }), []);

  const [infoModal, setInfoModal] = useState({ open: false, title: '', message: '' });
  const [confirm, setConfirm] = useState({ open: false, kind: null });
  const [sourceModal, setSourceModal] = useState({ open: false, mode: 'create', form: initialSourceForm });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, target: null });
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState({ open: false, target: null });
  const [clearHistoryConfirm, setClearHistoryConfirm] = useState(false);

  // deteta a transição "a sincronizar" -> terminado para mostrar a mensagem de conclusão
  // (mesmo depois de trocar de separador ou de grupo e voltar)
  const prevRunning = useRef({ website: false, events_promotions: false });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosAuth.get('/groups');
        setGroups(data || []);
      } catch (err) {
        openError('Erro ao carregar grupos', err);
      }
    })();
  }, [axiosAuth, openError]);

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: String(g.id), label: g.name })),
    [groups],
  );

  useEffect(() => {
    if (!groupId && groupOptions.length === 1) {
      setGroupId(groupOptions[0].value);
    }
  }, [groupOptions, groupId]);

  // hotéis do grupo selecionado, para o âmbito das fontes de Eventos & Promoções
  const [hotels, setHotels] = useState([]);
  useEffect(() => {
    setHotels([]);
    if (!groupId) return;
    (async () => {
      try {
        const { data } = await axiosAuth.get(`/hotels/group/${groupId}`);
        setHotels(data || []);
      } catch (err) {
        openError('Erro ao carregar hotéis', err);
      }
    })();
  }, [groupId, axiosAuth, openError]);

  const hotelOptions = useMemo(
    () => [
      { value: '', label: '— Grupo (todos os hotéis) —' },
      ...hotels.map((h) => ({ value: String(h.id), label: h.name })),
    ],
    [hotels],
  );
  const hotelNameById = useMemo(
    () => Object.fromEntries(hotels.map((h) => [String(h.id), h.name])),
    [hotels],
  );

  const fetchStatus = useCallback(async () => {
    if (!groupId) return;
    try {
      const { data } = await axiosAuth.get(`/groups/${groupId}/sync-status`);
      setStatus(data);
    } catch (err) {
      openError('Erro ao consultar estado da sincronização', err);
    }
  }, [groupId, axiosAuth, openError]);

  const fetchHistory = useCallback(async () => {
    if (!groupId) return;
    try {
      const { data } = await axiosAuth.get(`/groups/${groupId}/sync-history`, {
        params: { limit: 20 },
      });
      setHistory(data || []);
    } catch (err) {
      openError('Erro ao carregar histórico', err);
    }
  }, [groupId, axiosAuth, openError]);

  const fetchSources = useCallback(async () => {
    if (!groupId) return;
    try {
      const { data } = await axiosAuth.get(`/groups/${groupId}/sync-settings`);
      setSources(data || []);
    } catch (err) {
      openError('Erro ao carregar fontes de Eventos & Promoções', err);
    }
  }, [groupId, axiosAuth, openError]);

  useEffect(() => {
    setStatus(null);
    setHistory([]);
    setSources([]);
    prevRunning.current = { website: false, events_promotions: false };
    if (!groupId) return;
    fetchStatus();
    fetchHistory();
    fetchSources();
  }, [groupId, fetchStatus, fetchHistory, fetchSources]);

  // mostra a mensagem de conclusão (data/páginas/conteúdos, ou o erro) assim que uma
  // sincronização em curso termina — sem isto, o utilizador só percebia pela mudança
  // silenciosa do badge no card.
  const announceResult = useCallback((kind, run) => {
    const label = SYNC_TYPE_LABEL[kind] || kind;
    if (!run || run.status === 'error') {
      setErrorModal({
        open: true,
        title: `${label}: falha na sincronização`,
        message:
          run?.error_message ||
          'Não foi possível concluir a sincronização. O conteúdo existente foi preservado.',
      });
      return;
    }
    const suffix = kind === 'website' ? ' FAQs e conteúdos manuais não foram alterados.' : '';
    setInfoModal({
      open: true,
      title: `${label} sincronizado com sucesso`,
      message: `${run.pages_processed ?? 0} páginas processadas. ${run.chunks_indexed ?? 0} conteúdos atualizados.${suffix}`,
    });
  }, []);

  // sincroniza prevRunning na 1ª carga de cada grupo (depois do primeiro fetchStatus), para
  // não anunciar como "acabado de terminar" algo que já estava em curso ao entrar na página
  useEffect(() => {
    if (!status) return;
    prevRunning.current = {
      website: !!status?.website?.is_running,
      events_promotions: !!status?.events_promotions?.is_running,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // enquanto houver uma sincronização em curso, faz polling do estado/histórico
  useEffect(() => {
    const running = status?.website?.is_running || status?.events_promotions?.is_running;
    if (!groupId || !running) return;
    const id = setInterval(async () => {
      const { data } = await axiosAuth.get(`/groups/${groupId}/sync-status`);
      setStatus(data);
      fetchHistory();
      for (const kind of ['website', 'events_promotions']) {
        const was = prevRunning.current[kind];
        const now = data?.[kind]?.is_running;
        if (was && !now) {
          announceResult(kind, data?.[kind]?.last_run);
        }
        prevRunning.current[kind] = now;
      }
    }, 5000);
    return () => clearInterval(id);
  }, [status, groupId, axiosAuth, fetchHistory, announceResult]);

  function askSyncWebsite() {
    setConfirm({ open: true, kind: 'website' });
  }
  function askSyncEvents() {
    setConfirm({ open: true, kind: 'events' });
  }

  async function confirmSync() {
    const { kind } = confirm;
    setConfirm({ open: false, kind: null });
    setTriggering(true);
    try {
      if (kind === 'website') {
        const { data } = await axiosAuth.post(`/groups/${groupId}/sync-website`);
        setInfoModal({ open: true, title: 'Sincronização iniciada', message: data.message });
      } else {
        const { data } = await axiosAuth.post(`/groups/${groupId}/sync-events-promotions`);
        setInfoModal({ open: true, title: 'Sincronização iniciada', message: data.message });
      }
      await fetchStatus();
      await fetchHistory();
    } catch (err) {
      openError('Erro ao iniciar sincronização', err);
    } finally {
      setTriggering(false);
    }
  }

  function openSourceCreate() {
    setSourceModal({ open: true, mode: 'create', form: initialSourceForm });
  }
  function openSourceEdit(s) {
    setSourceModal({
      open: true,
      mode: 'edit',
      form: {
        id: s.id,
        url_pattern: s.url_pattern,
        description: s.description || '',
        is_active: s.is_active,
        hotel_id: s.hotel_id ? String(s.hotel_id) : '',
      },
    });
  }

  async function saveSource() {
    const { mode, form } = sourceModal;
    if (!form.url_pattern.trim()) {
      openError('Validação', new Error('Indique o URL ou padrão.'));
      return;
    }
    try {
      const payload = {
        url_pattern: form.url_pattern.trim(),
        description: form.description.trim() || null,
        is_active: form.is_active,
        hotel_id: form.hotel_id ? Number(form.hotel_id) : null,
      };
      if (mode === 'create') {
        await axiosAuth.post(`/groups/${groupId}/sync-settings`, payload);
      } else {
        await axiosAuth.put(`/groups/${groupId}/sync-settings/${form.id}`, payload);
      }
      setSourceModal({ open: false, mode: 'create', form: initialSourceForm });
      await fetchSources();
      await fetchStatus();
    } catch (err) {
      openError('Erro ao guardar fonte', err);
    }
  }

  function askDeleteSource(s) {
    setDeleteConfirm({ open: true, target: s });
  }

  async function confirmDeleteSource() {
    const { target } = deleteConfirm;
    try {
      await axiosAuth.delete(`/groups/${groupId}/sync-settings/${target.id}`);
      await fetchSources();
      await fetchStatus();
    } catch (err) {
      openError('Erro ao eliminar fonte', err);
    } finally {
      setDeleteConfirm({ open: false, target: null });
    }
  }

  function askDeleteHistoryRun(r) {
    setHistoryDeleteConfirm({ open: true, target: r });
  }

  async function confirmDeleteHistoryRun() {
    const { target } = historyDeleteConfirm;
    try {
      await axiosAuth.delete(`/groups/${groupId}/sync-history/${target.id}`);
      await fetchHistory();
    } catch (err) {
      openError('Erro ao eliminar registo do histórico', err);
    } finally {
      setHistoryDeleteConfirm({ open: false, target: null });
    }
  }

  async function confirmClearHistory() {
    try {
      await axiosAuth.delete(`/groups/${groupId}/sync-history`);
      await fetchHistory();
    } catch (err) {
      openError('Erro ao limpar histórico', err);
    } finally {
      setClearHistoryConfirm(false);
    }
  }

  const sourceColumns = useMemo(
    () => [
      { key: 'url_pattern', label: 'URL / padrão' },
      { key: 'description', label: 'Descrição', render: (s) => s.description || '—' },
      {
        key: 'scope',
        label: 'Âmbito',
        render: (s) => (s.hotel_id ? (hotelNameById[String(s.hotel_id)] || `Hotel #${s.hotel_id}`) : 'Grupo'),
      },
      { key: 'is_active', label: 'Ativo', render: (s) => (s.is_active ? 'Sim' : 'Não') },
      {
        key: 'actions',
        label: 'Ações',
        render: (s) => (
          <>
            <button className="action-button edit" onClick={() => openSourceEdit(s)}>
              <Edit />
            </button>
            <button className="action-button delete" onClick={() => askDeleteSource(s)}>
              <Close />
            </button>
          </>
        ),
      },
    ],
    [hotelNameById],
  );

  const historyColumns = useMemo(
    () => [
      { key: 'started_at', label: 'Data/Hora', render: (r) => formatDate(r.started_at) },
      { key: 'sync_type', label: 'Tipo', render: (r) => SYNC_TYPE_LABEL[r.sync_type] || r.sync_type },
      {
        key: 'status',
        label: 'Estado',
        render: (r) => (
          <span className={`badge ${STATUS_BADGE_CLASS[r.status] || ''}`}>
            {STATUS_LABEL[r.status] || r.status}
          </span>
        ),
      },
      { key: 'pages_processed', label: 'Páginas', render: (r) => r.pages_processed ?? '—' },
      {
        key: 'chunks',
        label: 'Conteúdos',
        render: (r) =>
          r.chunks_indexed || r.chunks_removed
            ? `${r.chunks_indexed ?? 0} indexados / ${r.chunks_removed ?? 0} removidos`
            : '—',
      },
      { key: 'duration_seconds', label: 'Duração', render: (r) => formatDuration(r.duration_seconds) },
      { key: 'error_message', label: 'Erro', render: (r) => r.error_message || '—' },
      {
        key: 'actions',
        label: '',
        render: (r) =>
          r.status === 'running' ? null : (
            <button
              className="action-button delete"
              title="Eliminar este registo"
              onClick={() => askDeleteHistoryRun(r)}
            >
              <Close />
            </button>
          ),
      },
    ],
    [],
  );

  const websiteRunning = status?.website?.is_running;
  const eventsRunning = status?.events_promotions?.is_running;
  const websiteLast = status?.website?.last_run;
  const eventsLast = status?.events_promotions?.last_run;

  return (
    <div className="sync-content-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {infoModal.open && (
        <AlertModal
          variant="info"
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ open: false, title: '', message: '' })}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title={confirm.kind === 'website' ? 'Sincronizar Website' : 'Sincronizar Eventos & Promoções'}
          variant="danger"
          message={
            confirm.kind === 'website'
              ? 'Será atualizado todo o conteúdo proveniente do website. Os FAQs e conteúdos manuais não serão alterados. Pretende continuar?'
              : 'Serão atualizados apenas os conteúdos classificados como Eventos & Promoções. Pretende continuar?'
          }
          secText="Cancelar"
          varText="Sincronizar"
          onCancel={() => setConfirm({ open: false, kind: null })}
          onConfirm={confirmSync}
        />
      )}
      {deleteConfirm.open && (
        <ConfirmationModal
          title="Eliminar fonte"
          variant="danger"
          message="Deseja eliminar esta fonte de Eventos & Promoções?"
          secText="Cancelar"
          varText="Eliminar"
          onCancel={() => setDeleteConfirm({ open: false, target: null })}
          onConfirm={confirmDeleteSource}
        />
      )}
      {historyDeleteConfirm.open && (
        <ConfirmationModal
          title="Eliminar registo"
          variant="danger"
          message="Deseja eliminar este registo do histórico de sincronizações?"
          secText="Cancelar"
          varText="Eliminar"
          onCancel={() => setHistoryDeleteConfirm({ open: false, target: null })}
          onConfirm={confirmDeleteHistoryRun}
        />
      )}
      {clearHistoryConfirm && (
        <ConfirmationModal
          title="Limpar histórico"
          variant="danger"
          message="Deseja eliminar todo o histórico de sincronizações, mantendo apenas o registo mais recente do Website e o das Eventos & Promoções?"
          secText="Cancelar"
          varText="Limpar"
          onCancel={() => setClearHistoryConfirm(false)}
          onConfirm={confirmClearHistory}
        />
      )}

      <PageTitle link="" title="Sincronização de Conteúdos" />

      {groupOptions.length > 1 && (
        <div className="group-selector">
          <DynamicSelect
            label="Grupo"
            placeholder="Escolher um grupo"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            options={groupOptions}
          />
        </div>
      )}

      {!groupId ? (
        <div className="empty-page">
          <p>Escolhe um grupo para gerir a sincronização de conteúdos.</p>
        </div>
      ) : (
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`tab-btn ${tab === t.value ? 'active' : ''}`}
                onClick={() => setTab(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'estado' && (
            <>
              <div className="sync-cards">
                <div className="sync-card">
                  <div className="sync-card-header">
                    <h3>Website</h3>
                    {websiteLast && (
                      <span className={`badge ${websiteRunning ? 'running' : STATUS_BADGE_CLASS[websiteLast.status] || ''}`}>
                        {websiteRunning ? STATUS_LABEL.running : STATUS_LABEL[websiteLast.status] || websiteLast.status}
                      </span>
                    )}
                  </div>
                  <p className="sync-card-url">
                    URL: {status?.website_url || <em>não configurado</em>}
                  </p>
                  {websiteLast ? (
                    <ul className="sync-card-details">
                      <li>Última sincronização: {formatDate(websiteLast.started_at)}</li>
                      <li>Páginas processadas: {websiteLast.pages_processed ?? '—'}</li>
                      <li>Conteúdos indexados: {websiteLast.chunks_indexed ?? '—'}</li>
                      <li>Duração: {formatDuration(websiteLast.duration_seconds)}</li>
                      {websiteLast.error_message && (
                        <li className="error-line">Erro: {websiteLast.error_message}</li>
                      )}
                    </ul>
                  ) : (
                    <p className="sync-card-empty">Ainda não foi feita nenhuma sincronização.</p>
                  )}
                  <Button
                    type="button"
                    disabled={!status?.website_url || websiteRunning || triggering}
                    onClick={askSyncWebsite}
                  >
                    {websiteRunning ? 'A sincronizar…' : 'Sincronizar Website'}
                  </Button>
                </div>

                <div className="sync-card">
                  <div className="sync-card-header">
                    <h3>Eventos & Promoções</h3>
                    {eventsLast && (
                      <span className={`badge ${eventsRunning ? 'running' : STATUS_BADGE_CLASS[eventsLast.status] || ''}`}>
                        {eventsRunning ? STATUS_LABEL.running : STATUS_LABEL[eventsLast.status] || eventsLast.status}
                      </span>
                    )}
                  </div>
                  <p className="sync-card-url">Fontes configuradas: {status?.sources_count ?? 0}</p>
                  {eventsLast ? (
                    <ul className="sync-card-details">
                      <li>Última sincronização: {formatDate(eventsLast.started_at)}</li>
                      <li>Páginas processadas: {eventsLast.pages_processed ?? '—'}</li>
                      <li>Conteúdos indexados: {eventsLast.chunks_indexed ?? '—'}</li>
                      <li>Duração: {formatDuration(eventsLast.duration_seconds)}</li>
                      {eventsLast.error_message && (
                        <li className="error-line">Erro: {eventsLast.error_message}</li>
                      )}
                    </ul>
                  ) : (
                    <p className="sync-card-empty">Ainda não foi feita nenhuma sincronização.</p>
                  )}
                  <Button
                    type="button"
                    disabled={!status?.sources_count || eventsRunning || triggering}
                    onClick={askSyncEvents}
                  >
                    {eventsRunning ? 'A sincronizar…' : 'Sincronizar Eventos & Promoções'}
                  </Button>
                  {!status?.sources_count && (
                    <p className="sync-card-hint">
                      Configura pelo menos uma fonte no separador "Fontes de Eventos & Promoções".
                    </p>
                  )}
                </div>
              </div>

              <div className="section-panel">
                <div className="section-panel-header">
                  <h3>Histórico de Sincronizações</h3>
                  {history.length > 1 && (
                    <button
                      type="button"
                      className="link-button danger"
                      onClick={() => setClearHistoryConfirm(true)}
                    >
                      Limpar histórico
                    </button>
                  )}
                </div>
                <div className="container">
                  <Table columns={historyColumns} data={history} />
                </div>
              </div>
            </>
          )}

          {tab === 'fontes' && (
            <div className="section-panel">
              <div className="section-header">
                <Button type="button" onClick={openSourceCreate}>
                  + Adicionar fonte
                </Button>
              </div>
              <div className="container">
                <Table columns={sourceColumns} data={sources} />
              </div>
            </div>
          )}
        </>
      )}

      {sourceModal.open && (
        <Modal
          isVisible={sourceModal.open}
          onClose={() => setSourceModal({ open: false, mode: 'create', form: initialSourceForm })}
          title={sourceModal.mode === 'create' ? 'Nova fonte' : 'Editar fonte'}
          onSave={saveSource}
          saveText={sourceModal.mode === 'create' ? 'Criar' : 'Guardar'}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveSource();
            }}
          >
            <div className="row">
              <DynamicInput
                type="text"
                label="URL ou padrão (ex: /eventos/, https://www.hotel.pt/eventos)"
                name="url_pattern"
                value={sourceModal.form.url_pattern}
                onChange={(name, value) =>
                  setSourceModal((p) => ({ ...p, form: { ...p.form, url_pattern: value } }))
                }
                required
              />
            </div>
            <div className="row">
              <DynamicInput
                type="text"
                label="Descrição (opcional)"
                name="description"
                value={sourceModal.form.description}
                onChange={(name, value) =>
                  setSourceModal((p) => ({ ...p, form: { ...p.form, description: value } }))
                }
              />
            </div>
            <div className="row">
              <DynamicSelect
                label="Âmbito"
                value={sourceModal.form.hotel_id}
                onChange={(e) =>
                  setSourceModal((p) => ({ ...p, form: { ...p.form, hotel_id: e.target.value } }))
                }
                options={hotelOptions}
              />
            </div>
            <div className="row checkbox-row">
              <DynamicCheckbox
                label="Ativo"
                name="is_active"
                checked={sourceModal.form.is_active}
                onChange={(name, value) =>
                  setSourceModal((p) => ({ ...p, form: { ...p.form, is_active: value } }))
                }
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
